import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { expendituresApi, filesApi, recurringApi } from "@/lib/api";
import CurrencyInput from "@/components/CurrencyInput";
import CurrencySelect from "@/components/CurrencySelect";
import { toast } from "sonner";
import { Paperclip, ExternalLink, X, Pause, Play, StopCircle } from "lucide-react";

const empty = {
  amount: 0, date: new Date().toISOString().slice(0,10), description: "", category: "General",
  vendor: "", source_client: "", kind: "expense", status: "", payment_due_date: "",
  project_id: "", attachment_file_id: "", attachment_url: "", notes: "",
  recurrence: "none", start_date: "", end_date: "", max_occurrences: "",
};

export default function ExpenditureDialog({ open, setOpen, exp, entityId, projects, onSaved, initialKind, entityCurrency }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const isRuleEdit = !!(exp?.id && exp?.__isRecurringRule);
  useEffect(() => {
    if (exp?.id && exp?.__isRecurringRule) {
      // Editing a RecurringTransaction rule — map cadence→recurrence and
      // give the form a `date` value so the shared UI works.
      setForm({
        ...empty,
        ...exp,
        date: exp.start_date || exp.date || new Date().toISOString().slice(0, 10),
        recurrence: exp.cadence || "monthly",
      });
    } else if (exp?.id) {
      setForm({ ...empty, ...exp });
    } else {
      // Editing new — respect initialKind and inherit currency from entity when available.
      const kind = exp?.kind || initialKind || "expense";
      setForm({
        ...empty,
        kind,
        currency: exp?.currency || entityCurrency || "USD",
        status: kind === "revenue" ? "Received" : "",
      });
    }
    setFile(null);
  }, [exp, open, initialKind, entityCurrency]);
  const set = (k,v) => setForm(f => ({ ...f, [k]: v }));
  const isRevenue = form.kind === "revenue";

  // When kind flips to revenue, ensure a default status.
  useEffect(() => {
    if (isRevenue && !form.status) set("status", "Received");
    if (!isRevenue && form.status) set("status", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.kind]);

  const uploadAttachment = async (f) => {
    setUploading(true);
    try {
      const r = await filesApi.upload(f);
      set("attachment_file_id", r.data.id);
      setFile(r.data);
      toast.success("File uploaded");
    } catch(e){ toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.amount || !form.date) return toast.error("Amount and date are required");
    setSaving(true);
    try {
      const isRecurring = (form.recurrence || "none") !== "none";
      if (isRecurring) {
        // Recurring rule path — create/update RecurringTransaction, don't
        // insert a one-off ledger row. First occurrence is lazily materialised
        // by _catch_up_recurring on the backend.
        const rulePayload = {
          entity_id: entityId,
          project_id: form.project_id || null,
          kind: form.kind,
          status: form.kind === "revenue" ? (form.status || "Received") : null,
          amount: Number(form.amount),
          currency: form.currency || "USD",
          cadence: form.recurrence,
          start_date: form.start_date || form.date,
          end_date: form.end_date || null,
          max_occurrences: form.max_occurrences ? Number(form.max_occurrences) : null,
          description: form.description || "",
          category: form.category || "General",
          vendor: form.vendor || "",
          source_client: form.source_client || "",
          notes: form.notes || "",
        };
        if (exp?.id && exp?.__isRecurringRule) {
          await recurringApi.update(exp.id, rulePayload);
        } else {
          await recurringApi.create(rulePayload);
        }
        toast.success(`Recurring ${form.kind} scheduled`);
        onSaved?.();
        return;
      }
      const payload = { ...form, entity_id: entityId, amount: Number(form.amount) };
      if (!payload.project_id) payload.project_id = null;
      if (!payload.attachment_file_id) payload.attachment_file_id = null;
      if (!payload.attachment_url) payload.attachment_url = null;
      if (!payload.payment_due_date) payload.payment_due_date = null;
      if (!isRevenue) { payload.status = null; payload.source_client = ""; payload.payment_due_date = null; }
      // Never persist recurrence scaffolding fields onto a one-off Expenditure.
      delete payload.recurrence; delete payload.start_date; delete payload.end_date; delete payload.max_occurrences;
      if (exp?.id) await expendituresApi.update(exp.id, payload);
      else await expendituresApi.create(payload);
      toast.success("Saved"); onSaved?.();
    } catch(e){ toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const ruleAction = async (kind) => {
    if (!isRuleEdit) return;
    try {
      if (kind === "pause") await recurringApi.pause(exp.id);
      if (kind === "resume") await recurringApi.resume(exp.id);
      if (kind === "end") {
        if (!window.confirm("End this recurrence? No more occurrences will be generated. Historical transactions are kept.")) return;
        await recurringApi.end(exp.id);
      }
      toast.success(kind === "end" ? "Recurrence ended" : (kind === "pause" ? "Paused" : "Resumed"));
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const dialogTitle = isRuleEdit
    ? (isRevenue ? "Edit recurring revenue" : "Edit recurring expense")
    : (exp?.id
      ? (isRevenue ? "Edit revenue" : "Edit expense")
      : (isRevenue ? "New revenue" : "New expense"));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#151A23] border-white/10 text-white max-w-lg" data-testid="expenditure-dialog">
        <DialogHeader><DialogTitle className="font-display" data-testid="expenditure-dialog-title">{dialogTitle}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Kind</Label>
            <Select value={form.kind} onValueChange={v=>set("kind", v)}>
              <SelectTrigger data-testid="exp-kind-select" className="mt-1.5 bg-white/5 border-white/10"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="expense">expense</SelectItem>
                <SelectItem value="revenue">revenue</SelectItem>
                <SelectItem value="expected">expected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isRevenue && (
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">Status</Label>
              <Select value={form.status || "Received"} onValueChange={v=>set("status", v)}>
                <SelectTrigger data-testid="exp-status-select" className="mt-1.5 bg-white/5 border-white/10"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#151A23] border-white/10 text-white">
                  <SelectItem value="Received">Received</SelectItem>
                  <SelectItem value="Outstanding">Outstanding</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Amount</Label>
            <div className="mt-1.5 flex items-stretch gap-2">
              <div className="flex-1 min-w-0">
                <CurrencyInput data-testid="exp-amount-input" value={form.amount} currency={form.currency || "USD"} onChange={(v)=>set("amount", v)} className="bg-white/5 border-white/10 h-10"/>
              </div>
              <div className="w-24 flex-shrink-0">
                <CurrencySelect value={form.currency || "USD"} onChange={(v)=>set("currency", v)}/>
              </div>
            </div>
          </div>
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Date</Label><Input type="date" value={form.date} onChange={e=>set("date", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div><Label className="text-white/60 text-xs uppercase tracking-wider">Category</Label><Input value={form.category || ""} onChange={e=>set("category", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          <div className="sm:col-span-2"><Label className="text-white/60 text-xs uppercase tracking-wider">Description</Label><Input value={form.description || ""} onChange={e=>set("description", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/></div>
          {isRevenue ? (
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">Source / Client</Label>
              <Input data-testid="exp-source-client-input" value={form.source_client || ""} onChange={e=>set("source_client", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/>
            </div>
          ) : (
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">Vendor / payee</Label>
              <Input value={form.vendor || ""} onChange={e=>set("vendor", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/>
            </div>
          )}
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Project</Label>
            <Select value={form.project_id || "__none"} onValueChange={v=>set("project_id", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="exp-project-select" className="mt-1.5 bg-white/5 border-white/10"><SelectValue placeholder="None"/></SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="__none">Entity-level (no project)</SelectItem>
                {projects?.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isRevenue && form.status === "Outstanding" && (
            <div className="sm:col-span-2">
              <Label className="text-white/60 text-xs uppercase tracking-wider">Payment due date (optional)</Label>
              <Input data-testid="exp-payment-due-input" type="date" value={form.payment_due_date || ""} onChange={e=>set("payment_due_date", e.target.value)} className="mt-1.5 bg-white/5 border-white/10"/>
            </div>
          )}
          <div className="sm:col-span-2 mt-1 p-3 rounded-lg border border-white/10 bg-white/[0.03]">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Recurring</Label>
            <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select value={form.recurrence || "none"} onValueChange={v=>set("recurrence", v)}>
                <SelectTrigger data-testid="exp-recurrence-select" className="bg-white/5 border-white/10"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#151A23] border-white/10 text-white">
                  <SelectItem value="none">No — one-off</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
              {(form.recurrence || "none") !== "none" && (
                <>
                  <Input type="date" value={form.start_date || form.date} onChange={e=>set("start_date", e.target.value)} className="bg-white/5 border-white/10" placeholder="Start date" data-testid="exp-recurring-start"/>
                  <Input type="date" value={form.end_date || ""} onChange={e=>set("end_date", e.target.value)} className="bg-white/5 border-white/10" placeholder="End date (optional)" data-testid="exp-recurring-end"/>
                  <Input type="number" min="1" value={form.max_occurrences || ""} onChange={e=>set("max_occurrences", e.target.value)} className="bg-white/5 border-white/10" placeholder="Max occurrences (optional)" data-testid="exp-recurring-max"/>
                </>
              )}
            </div>
            {(form.recurrence || "none") !== "none" && (
              <div className="text-[11px] text-white/40 mt-2">
                Nothing is counted in Spent/Revenue/Outstanding until each occurrence date arrives — a real transaction is generated then.
              </div>
            )}
          </div>
          <div className="sm:col-span-2">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Notes (optional)</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={e=>set("notes", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 text-sm"/>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Attachment ({isRevenue ? "receipt/invoice" : "receipt"})</Label>
            <div className="mt-1.5 space-y-2">
              {form.attachment_file_id && (
                <div className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/10 text-sm">
                  <div className="flex items-center gap-2"><Paperclip className="w-3.5 h-3.5"/> {file?.original_filename || "Attached file"}</div>
                  <button type="button" onClick={() => { set("attachment_file_id",""); setFile(null); }} className="p-1 hover:bg-white/10 rounded"><X className="w-3.5 h-3.5"/></button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <label className="flex-1 cursor-pointer">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAttachment(e.target.files[0])} data-testid="exp-file-input"/>
                  <div className="px-3 py-2 rounded bg-white/5 border border-white/10 text-sm text-white/70 hover:bg-white/10 text-center">{uploading ? "Uploading…" : "Upload file (PDF, JPG, PNG, DOCX, XLSX)"}</div>
                </label>
              </div>
              <Input placeholder="Or paste an external URL" value={form.attachment_url || ""} onChange={e=>set("attachment_url", e.target.value)} className="bg-white/5 border-white/10"/>
            </div>
          </div>
        </div>
        {isRuleEdit && (
          <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-white/10 pt-3" data-testid="rule-actions">
            <div className="text-[11px] uppercase tracking-wider text-white/40 mr-1">Rule</div>
            {!exp?.paused && !exp?.ended && (
              <Button variant="outline" size="sm" onClick={() => ruleAction("pause")} data-testid="rule-pause-btn" className="rounded-full border-amber-400/40 text-amber-300 hover:bg-amber-500/10">
                <Pause className="w-3.5 h-3.5 mr-1.5"/>Pause
              </Button>
            )}
            {exp?.paused && !exp?.ended && (
              <Button variant="outline" size="sm" onClick={() => ruleAction("resume")} data-testid="rule-resume-btn" className="rounded-full border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10">
                <Play className="w-3.5 h-3.5 mr-1.5"/>Resume
              </Button>
            )}
            {!exp?.ended && (
              <Button variant="outline" size="sm" onClick={() => ruleAction("end")} data-testid="rule-end-btn" className="rounded-full border-red-400/40 text-red-300 hover:bg-red-500/10">
                <StopCircle className="w-3.5 h-3.5 mr-1.5"/>End recurrence
              </Button>
            )}
            {exp?.ended && (
              <div className="text-xs text-white/50 italic">This recurrence has ended. Historical transactions are preserved.</div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="exp-save-btn" className="rounded-full bg-white text-black hover:bg-white/90">{saving?"Saving…":"Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
